import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Wand2, Plus, Loader2, Sparkles } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { api } from "@/lib/api";
import { CATEGORIES, newLesson, apiError } from "@/lib/campus";
import { TemplatePicker } from "@/components/campus/TemplatePicker";
import { LessonEditor } from "@/components/campus/LessonEditor";
import { ContactVerify } from "@/components/campus/ContactVerify";

const field = "w-full bg-[#0B0E14] border border-white/10 rounded-lg px-3.5 py-2.5 text-sm text-white focus:outline-none focus:border-amber-500";

const AiOutline = ({ format, onOutline }) => {
  const [topic, setTopic] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/course-outline", { topic, format });
      onOutline(data);
      toast.success("Outline ready — now fill it in with your own expertise");
    } catch (err) {
      toast.error(apiError(err, "Couldn't build an outline"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rounded-2xl border border-amber-500/30 bg-amber-500/[0.06] p-5" data-testid="ai-outline">
      <div className="flex items-center gap-2 text-amber-300 font-semibold text-sm mb-1"><Wand2 className="h-4 w-4" /> AI outline helper</div>
      <p className="text-xs text-slate-400 mb-3">Get a lesson structure in seconds. Write the teaching yourself — courses estimated over 45% AI-written are rejected (unless the topic is AI).</p>
      <div className="flex gap-2">
        <input data-testid="ai-outline-topic" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="What do you want to teach?" className={field} />
        <button type="button" data-testid="ai-outline-btn" onClick={run} disabled={busy || topic.trim().length < 3}
          className="shrink-0 px-4 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 text-sm font-bold disabled:opacity-50 flex items-center gap-1.5">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Outline
        </button>
      </div>
    </div>
  );
};

export default function CourseBuilder() {
  const navigate = useNavigate();
  const [fmt, setFmt] = useState(null);
  const [info, setInfo] = useState({ title: "", category: CATEGORIES[0], price: 9, description: "" });
  const [lessons, setLessons] = useState([]);
  const [prompts, setPrompts] = useState({});
  const [email, setEmail] = useState("");
  const [verified, setVerified] = useState(false);
  const [saving, setSaving] = useState(false);
  const [future, setFuture] = useState([]);

  const pick = (f) => {
    setFmt(f.value);
    setLessons(f.lessons.map((t) => newLesson(t)));
    setPrompts({});
  };
  const applyOutline = (o) => {
    setInfo((i) => ({ ...i, title: o.title || i.title, description: o.description || i.description }));
    const ls = (o.lessons || []).map((l) => ({ ...newLesson(l.title), duration: l.duration || "" }));
    setLessons(ls);
    setPrompts(Object.fromEntries(ls.map((l, i) => [l.id, o.lessons[i].prompts || []])));
    setFuture(o.future_episodes || []);
  };
  const label = fmt === "series" ? "Episode" : "Lesson";
  const multi = fmt === "course" || fmt === "series" || fmt === "class";

  const submit = async () => {
    if (!info.title || !info.description) return toast.error("Add a title and description");
    if (!verified) return toast.error("Verify your contact email first");
    if (Number(info.price) < 2) return toast.error("Minimum price is $2");
    setSaving(true);
    try {
      const { data } = await api.post("/courses", { ...info, price: Number(info.price), format: fmt, contact_email: email, lessons });
      if (data.status === "published") toast.success(data.marketable_skill ? "Published — tagged as a marketable skill!" : "Published!");
      else if (data.status === "pending") toast.success("Submitted — our team will review it shortly");
      else return toast.error(`Not approved: ${data.reason || "didn't meet our quality bar"}`);
      navigate("/teaching");
    } catch (err) {
      toast.error(apiError(err, "Couldn't submit"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0E14] text-slate-100">
      <Navbar />
      <div className="max-w-5xl mx-auto px-5 md:px-10 py-10 space-y-10" data-testid="course-builder">
        <div>
          <div className="text-amber-400 text-xs font-semibold uppercase tracking-[0.2em] mb-2">Course builder</div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight">What are you teaching?</h1>
          <p className="text-slate-400 mt-3 max-w-2xl text-sm md:text-base">Pick a format. Upload video, audio or slides to any lesson. Set a price from $2 and keep 93%.</p>
        </div>

        <TemplatePicker value={fmt} onPick={pick} />

        {fmt && (
          <>
            <AiOutline format={fmt} onOutline={applyOutline} />

            <section className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-slate-300 mb-1">Title</label>
                <input data-testid="builder-title" value={info.title} onChange={(e) => setInfo({ ...info, title: e.target.value })} placeholder="e.g. Freelance Bookkeeping in Excel" className={field} />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-slate-300 mb-1">Description — what can students DO afterwards?</label>
                <textarea data-testid="builder-description" rows={3} value={info.description} onChange={(e) => setInfo({ ...info, description: e.target.value })} className={field} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">Category</label>
                <select data-testid="builder-category" value={info.category} onChange={(e) => setInfo({ ...info, category: e.target.value })} className={field}>
                  {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">Price (USD, $2 minimum){fmt === "series" && " — one price unlocks every future episode"}</label>
                <input data-testid="builder-price" type="number" min="2" step="1" value={info.price} onChange={(e) => setInfo({ ...info, price: e.target.value })} className={field} />
                <p className="text-xs text-amber-300/80 mt-1" data-testid="builder-earnings">You earn ${(Number(info.price || 0) * 0.93).toFixed(2)} per student (7% platform fee).</p>
              </div>
            </section>

            <section className="space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-white">{label}s</h2>
                {multi && (
                  <button type="button" data-testid="builder-add-lesson" onClick={() => setLessons([...lessons, newLesson("")])}
                    className="flex items-center gap-1 text-sm text-amber-300 hover:text-amber-200"><Plus className="h-4 w-4" /> Add {label.toLowerCase()}</button>
                )}
              </div>
              {lessons.map((l, i) => (
                <LessonEditor key={l.id} lesson={l} index={i} label={label} prompts={prompts[l.id]}
                  onChange={(nl) => setLessons(lessons.map((x) => (x.id === l.id ? nl : x)))}
                  onRemove={lessons.length > 1 ? () => setLessons(lessons.filter((x) => x.id !== l.id)) : null} />
              ))}
              {fmt === "series" && future.length > 0 && (
                <div className="text-xs text-slate-400 rounded-xl border border-white/10 p-4" data-testid="builder-future-episodes">
                  <div className="text-slate-300 font-semibold mb-1">Ideas for future episodes</div>
                  <ul className="list-disc pl-5 space-y-0.5">{future.map((f) => <li key={f}>{f}</li>)}</ul>
                </div>
              )}
            </section>

            <section className="rounded-2xl bg-[#121721] border border-white/10 p-5 space-y-4">
              <ContactVerify email={email} setEmail={setEmail} verified={verified} setVerified={setVerified} />
              <button type="button" data-testid="builder-submit" onClick={submit} disabled={saving || !verified}
                className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold py-3.5 rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} {saving ? "AI review in progress…" : "Submit for review & publish"}
              </button>
              <p className="text-xs text-slate-500 text-center">Every submission is checked for real teaching value. Clear wins go live instantly; borderline ones get a human review.</p>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
